import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from '@/components/ui/breadcrumb';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Separator } from '@/components/ui/separator';
import { SidebarTrigger } from '@/components/ui/sidebar';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

import { createFileRoute, Link, useNavigate, useParams } from '@tanstack/react-router'
import { EllipsisVerticalIcon } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { useState, useEffect } from 'react';
import {
  updateUserGroupMutation,
  getUserGroupQuery,
  getModuleDefinitionsQuery,
  setModuleAccessMutation
} from '@/queries/userGroupMutation';
import type { UserGroupData } from '@/queries/userGroupMutation';

export const Route = createFileRoute('/_app/settings/user-groups/$id/')({
  component: RouteComponent,
})

function RouteComponent() {
  const { id } = useParams({ from: '/_app/settings/user-groups/$id/' });
  const { data: userGroupData, isLoading } = useQuery(getUserGroupQuery(id));
  const { data: moduleDefinitions = [] } = useQuery(getModuleDefinitionsQuery());
  const updateUserGroup = updateUserGroupMutation();
  const setModuleAccess = setModuleAccessMutation();
  const navigate = useNavigate();

  const [formData, setFormData] = useState<UserGroupData>({
    id: '',
    name: '',
    active: true,
    moduleSecurity: [],
    entitySecurity: []
  });

  const [modulePermissions, setModulePermissions] = useState<Record<string, boolean>>({});
  const [entityPermissions, setEntityPermissions] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (userGroupData) {
      setFormData(userGroupData);

      // Build permission objects from the data
      const modulePerms: Record<string, boolean> = {};
      const entityPerms: Record<string, boolean> = {};

      // Process module security
      userGroupData.moduleSecurity?.forEach(ms => {
        modulePerms[ms.moduleId] = ms.allow;
      });

      // Process entity security
      userGroupData.entitySecurity?.forEach(es => {
        entityPerms[`${es.entityTypeId}_view`] = es.allowView;
        entityPerms[`${es.entityTypeId}_create`] = es.allowCreate;
        entityPerms[`${es.entityTypeId}_update`] = es.allowUpdate;
        entityPerms[`${es.entityTypeId}_delete`] = es.allowDelete;
        entityPerms[`${es.entityTypeId}_import`] = es.allowImport;
        entityPerms[`${es.entityTypeId}_merge`] = es.allowMerge;
      });

      setModulePermissions(modulePerms);
      setEntityPermissions(entityPerms);
    }
  }, [userGroupData, moduleDefinitions]);

  // Initialize entity permissions for all module definitions
  useEffect(() => {
    if (moduleDefinitions.length > 0) {
      const updatedEntityPerms = { ...entityPermissions };

      moduleDefinitions.forEach(moduleDef => {
        if (moduleDef.entityDefinitions) {
          moduleDef.entityDefinitions.forEach(entityDef => {
            const permissions = ['view', 'create', 'update', 'delete', 'import', 'merge'];
            permissions.forEach(permission => {
              const key = `${entityDef.id}_${permission}`;
              if (!(key in updatedEntityPerms)) {
                updatedEntityPerms[key] = false;
              }
            });
          });
        }
      });

      setEntityPermissions(updatedEntityPerms);
    }
  }, [moduleDefinitions]);

  const handleSave = async () => {
    try {
      // Update formData with current entity permissions
      const entitySecurity = Object.keys(entityPermissions)
        .filter(key => entityPermissions[key])
        .reduce((acc, key) => {
          const [entityTypeId, permission] = key.split('_');
          const existing = acc.find(es => es.entityTypeId === entityTypeId);

          if (existing) {
            (existing as any)[`allow${permission.charAt(0).toUpperCase() + permission.slice(1)}`] = true;
          } else {
            const newEntity: any = {
              entityTypeId,
              allowView: false,
              allowCreate: false,
              allowUpdate: false,
              allowDelete: false,
              allowImport: false,
              allowMerge: false,
            };
            newEntity[`allow${permission.charAt(0).toUpperCase() + permission.slice(1)}`] = true;
            acc.push(newEntity);
          }

          return acc;
        }, [] as any[]);

      // Ensure all entity types are represented (even with all false permissions)
      moduleDefinitions.forEach(moduleDef => {
        if (moduleDef.entityDefinitions) {
          moduleDef.entityDefinitions.forEach(entityDef => {
            const existing = entitySecurity.find(es => es.entityTypeId === entityDef.id);
            if (!existing) {
              entitySecurity.push({
                entityTypeId: entityDef.id,
                allowView: false,
                allowCreate: false,
                allowUpdate: false,
                allowDelete: false,
                allowImport: false,
                allowMerge: false,
              });
            }
          });
        }
      });

      const updatedFormData = {
        ...formData,
        entitySecurity
      };

      await updateUserGroup.mutateAsync({ id, data: updatedFormData });
      navigate({ to: '/settings/user-groups' });
    } catch (error) {
      console.error('Failed to update user group:', error);
    }
  };

  const handleModuleChange = async (moduleId: string, allow: boolean) => {
    setModulePermissions(prev => ({ ...prev, [moduleId]: allow }));

    try {
      await setModuleAccess.mutateAsync({
        profileId: id,
        moduleId,
        allowAccess: allow
      });
    } catch (error) {
      console.error('Failed to update module access:', error);
    }
  };

  const handleEntityPermissionChange = (entityId: string, permission: string, value: boolean) => {
    const key = `${entityId}_${permission}`;
    setEntityPermissions(prev => ({ ...prev, [key]: value }));
  };

  const handleSelectAllForModule = (moduleId: string) => {
    const module = moduleDefinitions.find(m => m.id === moduleId);
    if (!module?.entityDefinitions) return;

    const updatedPermissions = { ...entityPermissions };
    const permissions = ['view', 'create', 'update', 'delete', 'import', 'merge'];

    // Set all permissions to true for this module's entities
    for (const entityDef of module.entityDefinitions) {
      for (const permission of permissions) {
        const key = `${entityDef.id}_${permission}`;
        updatedPermissions[key] = true;
      }
    }

    setEntityPermissions(updatedPermissions);
  };

  const handleDeselectAllForModule = (moduleId: string) => {
    const module = moduleDefinitions.find(m => m.id === moduleId);
    if (!module?.entityDefinitions) return;

    const updatedPermissions = { ...entityPermissions };
    const permissions = ['view', 'create', 'update', 'delete', 'import', 'merge'];

    // Set all permissions to false for this module's entities
    for (const entityDef of module.entityDefinitions) {
      for (const permission of permissions) {
        const key = `${entityDef.id}_${permission}`;
        updatedPermissions[key] = false;
      }
    }

    setEntityPermissions(updatedPermissions);
  };

  const handleColumnPermissionChange = (moduleId: string, permission: string, value: boolean) => {
    const module = moduleDefinitions.find(m => m.id === moduleId);
    if (!module?.entityDefinitions) return;

    const updatedPermissions = { ...entityPermissions };

    // Set the specific permission for all entities in this module
    for (const entityDef of module.entityDefinitions) {
      const key = `${entityDef.id}_${permission}`;
      updatedPermissions[key] = value;
    }

    setEntityPermissions(updatedPermissions);
  };

  const isColumnChecked = (moduleId: string, permission: string): boolean => {
    const module = moduleDefinitions.find(m => m.id === moduleId);
    if (!module?.entityDefinitions) return false;

    return module.entityDefinitions.every(entityDef =>
      entityPermissions[`${entityDef.id}_${permission}`] || false
    );
  };

  const isColumnIndeterminate = (moduleId: string, permission: string): boolean => {
    const module = moduleDefinitions.find(m => m.id === moduleId);
    if (!module?.entityDefinitions) return false;

    const checkedCount = module.entityDefinitions.filter(entityDef =>
      entityPermissions[`${entityDef.id}_${permission}`] || false
    ).length;

    return checkedCount > 0 && checkedCount < module.entityDefinitions.length;
  };

  if (isLoading) {
    return <div>Loading...</div>;
  }

  return (
    <>
      <header
        className={`sticky top-0 flex shrink-0 items-center gap-2 p-2 bg-white border-b-4 border-l-gray-200 border-r-gray-200 border-t-gray-200 border-t-2`}
      >
        <SidebarTrigger className="-ml-1" />
        <Separator orientation="vertical" className="mr-2 h-4" />
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem className="hidden md:block">
              <BreadcrumbLink asChild>
                <Link to='/settings/user-groups'>
                  User Groups
                </Link>
              </BreadcrumbLink>
            </BreadcrumbItem>

            <BreadcrumbSeparator className="hidden md:block" />
            <BreadcrumbItem>
              <BreadcrumbPage>
                Edit User Group
              </BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
        <div className="flex items-center gap-2 ml-auto mr-5">
          <Button
            onClick={handleSave}
            disabled={updateUserGroup.isPending}
            className="bg-green-500 hover:bg-green-600 text-white"
          >
            {updateUserGroup.isPending ? 'Saving...' : 'Save'}
          </Button>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline">
                Actions <EllipsisVerticalIcon className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuLabel>Quick Actions</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem className="text-red-500">Delete Group</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      <div className="h-full w-full p-6 bg-gray-100 overflow-auto">
        <Card className="max-w-6xl">
          <CardHeader>
            <CardTitle className="text-lg font-medium">Edit User Group</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Name Input */}
            <div className="max-w-md">
              <Label htmlFor="name" className="text-sm font-medium">
                Name
              </Label>
              <Input
                id="name"
                value={formData.name || ''}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                className="mt-1"
              />
            </div>

            {/* Module Tabs */}
            <div className="space-y-4">
              <Tabs defaultValue={moduleDefinitions[0]?.id} className="w-full">
                <TabsList className="grid w-full grid-cols-9 h-auto p-1">
                  {moduleDefinitions.map((module) => (
                    <TabsTrigger
                      key={module.id}
                      value={module.id}
                      className="text-xs px-2 py-2"
                    >
                      {module.name}
                    </TabsTrigger>
                  ))}
                </TabsList>

                {moduleDefinitions.map((module) => (
                  <TabsContent key={module.id} value={module.id} className="space-y-4">
                    {/* Module Access Control */}
                    <Card>
                      <CardContent>
                        <div className="space-y-4">
                          <div>
                            <Label className="text-sm font-medium">
                              {module.name} Access
                            </Label>
                            <Select
                              value={modulePermissions[module.id] ? 'true' : 'false'}
                              onValueChange={(value) => handleModuleChange(module.id, value === 'true')}
                            >
                              <SelectTrigger className="mt-1 max-w-xs">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="true">Allow</SelectItem>
                                <SelectItem value="false">Deny</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Entity Permissions Table */}
                    {modulePermissions[module.id] && module.entityDefinitions && (
                      <div className="bg-white border rounded-lg">
                        <div className="p-4 border-b bg-gray-50 flex justify-between items-center">
                          <h3 className="text-sm font-medium text-gray-900">Entity Permissions</h3>
                          <div className="flex gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleSelectAllForModule(module.id)}
                            >
                              Select All
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDeselectAllForModule(module.id)}
                            >
                              Deselect All
                            </Button>
                          </div>
                        </div>
                        <div className="overflow-x-auto">
                          <table className="w-full">
                            <thead className="bg-gray-50 border-b">
                              <tr>
                                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">
                                  Collection
                                </th>
                                {['view', 'create', 'update', 'delete', 'import', 'merge'].map((permission) => (
                                  <th key={permission} className="px-4 py-3 text-center text-sm font-semibold text-gray-900">
                                    <div className="flex flex-col items-center gap-1">
                                      <span className="capitalize">{permission}</span>
                                      <Checkbox
                                        checked={isColumnChecked(module.id, permission)}
                                        onCheckedChange={(checked) =>
                                          handleColumnPermissionChange(module.id, permission, checked as boolean)
                                        }
                                        className="h-4 w-4"
                                        data-indeterminate={isColumnIndeterminate(module.id, permission)}
                                      />
                                    </div>
                                  </th>
                                ))}
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                              {module.entityDefinitions.map((entityDef) => (
                                <tr key={entityDef.id} className="hover:bg-gray-50">
                                  <td className="px-4 py-3 text-sm text-gray-900">
                                    {entityDef.name}
                                  </td>
                                  {['view', 'create', 'update', 'delete', 'import', 'merge'].map((permission) => (
                                    <td key={permission} className="px-4 py-3 text-center">
                                      <Checkbox
                                        checked={entityPermissions[`${entityDef.id}_${permission}`] || false}
                                        onCheckedChange={(checked) =>
                                          handleEntityPermissionChange(entityDef.id, permission, checked as boolean)
                                        }
                                      />
                                    </td>
                                  ))}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </TabsContent>
                ))}
              </Tabs>
            </div>
          </CardContent>
        </Card>
      </div>
    </>
  );
}
